// @ts-check
import { DISPATCH_EVENTS_PREFIX, DelayTimer, DispatchEvents, ViewMax } from "consts";
import React, { Children, Component } from "react";
import { dispatchEvent, isArray, removeNodeIfContained, widgetStyling } from "utils";

import { createPortal } from "react-dom";

/**
 * @typedef {import('preact').ComponentChild} ComponentChild
 * @typedef {import('preact').JSX.CSSProperties} CSSProperties
 */

/**
 * @typedef {Object} FrameProps
 * @property {string} id - Unique identifier for the frame
 * @property {string} name - Name attribute for the iframe
 * @property {string} instanceId - Widget instance identifier
 * @property {string} stylePre - CSS class prefix for styling
 * @property {CSSProperties} [style] - Inline styles for the iframe
 * @property {string} [headStyle] - CSS to inject into iframe head
 * @property {string} [className] - CSS class name for the iframe
 * @property {string} [title] - Title/aria-label for the iframe
 * @property {ComponentChild} [children] - Content to render inside the iframe
 * @property {Array<{href: string, id: string}>} [cssHrefs] - CSS files to load in iframe
 * @property {boolean} [isResizable] - Whether iframe should auto-resize
 * @property {string} [frameHeight] - Fixed height for non-resizable frames
 */

/**
 * Frame component that creates an isolated iframe environment for widget content
 * @extends {Component<FrameProps>}
 */
class Frame extends Component {
  /** @type {HTMLDivElement} */
  wrap;
  /** @type {HTMLLinkElement[]} */
  cssLinksArray;
  /** @type {HTMLStyleElement | null} */
  styles;
  /** @type {HTMLIFrameElement | null} */
  frameRef;
  /** @type {import('preact').ComponentChild | null} */
  fPortal;
  /** @type {Window | null} */
  fTopParent;
  /** @type {number | null} */
  frameLoadInterval;
  /** @type {MutationObserver | null} */
  frameObserver;

  /**
   * @param {FrameProps} props
   */
  constructor(props) {
    super(props);
    this.wrap = document.createElement("div");
    this.cssLinksArray = [];
    this.styles = null;
    this.frameRef = null;
    this.fPortal = null;
    this.fTopParent = null;
    this.frameLoadInterval = null;
    this.frameObserver = null;
    this.state = { fPortalCreated: false };
  }

  componentDidMount() {
    if (this.getFrameDocument()) {
      this.frameLoadInterval = window.setInterval(() => this.onFrameLoadInterval(), DelayTimer.FrameLoadIntervalMS);

      /** @type {Window} */
      let fTopParent = window;
      while (fTopParent.parent && fTopParent.parent !== fTopParent) {
        fTopParent = fTopParent.parent;
      }
      this.fTopParent = fTopParent;
    }
  }

  componentWillUnmount() {
    const frameDocument = this.getFrameDocument();

    if (frameDocument) {
      window.removeEventListener("resize", () => this.frameAdaptiveListener());
      window.removeEventListener(`${DISPATCH_EVENTS_PREFIX}${DispatchEvents.ForceFrameResize}`, () =>
        this.frameAdaptiveListener(),
      );

      this.frameObserver && this.frameObserver.disconnect && this.frameObserver.disconnect();

      for (let cssLa = 0; cssLa < this.cssLinksArray.length; cssLa++) {
        removeNodeIfContained(this.cssLinksArray[cssLa], frameDocument.head);
      }
      removeNodeIfContained(this.wrap, frameDocument.body);
      removeNodeIfContained(this.styles, frameDocument.head);
    }
    this.frameRef = null;
  }

  onFrameLoadInterval() {
    const { id, cssHrefs, headStyle, isResizable, stylePre } = this.props;
    const frameDocument = this.getFrameDocument();

    /**
     * @param {MutationRecord[]} mutations
     */
    const frameMutationObserver = (mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" || mutation.type === "attributes") {
          this.frameAdaptiveListener();
        }
      });
    };

    // NOTE: Due to FireFox iframe loading life-cycle,
    // we use a window.setInterval to wait for when the iframe document readyState is "complete",
    // then, clearInterval and load/append iframe children;
    if (this.frameLoadInterval && frameDocument && frameDocument.readyState === "complete") {
      window.clearInterval(this.frameLoadInterval);

      if (isArray(cssHrefs)) {
        for (let cssH = 0; cssH < cssHrefs.length; cssH++) {
          const cssLink = document.createElement("link");
          cssLink.rel = "stylesheet";
          cssLink.href = cssHrefs[cssH].href;
          cssLink.id = cssHrefs[cssH].id;
          this.cssLinksArray.push(cssLink);
          frameDocument.head.appendChild(cssLink);
        }
      }

      if (headStyle) {
        const cleanHeadStyle = widgetStyling.sanitizeStyles(headStyle);
        if (cleanHeadStyle) {
          this.styles = document.createElement("style");
          this.styles.id = `${stylePre}head-styles_${id}`;
          this.styles.textContent = `${cleanHeadStyle}`;
          frameDocument.head.appendChild(this.styles);
        }
      }

      this.wrap.id = `${stylePre}content-wrapper_${id}`;
      this.wrap.className = `${stylePre}content-wrapper`;

      const resizableClass = `${stylePre}html${isResizable ? "-" : "-not-"}resizable`;
      frameDocument.documentElement.className = `${stylePre}html ${resizableClass}`;
      frameDocument.body.id = `${stylePre}body_${id}`;
      frameDocument.body.className = `${stylePre}body`;

      frameDocument.body.appendChild(this.wrap);

      const frameResizeListener = () => this.frameAdaptiveListener();

      window.addEventListener("resize", frameResizeListener, { passive: true });
      window.addEventListener(`${DISPATCH_EVENTS_PREFIX}${DispatchEvents.ForceFrameResize}`, frameResizeListener, {
        passive: true,
      });

      this.frameObserver = new MutationObserver(frameMutationObserver);

      this.frameObserver.observe(this.wrap, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });

      this.createFramePortal();
    }
  }

  getFrameDocument() {
    return this.frameRef ? this.frameRef.contentDocument : null;
  }

  /**
   * @param {Window | undefined} fTopParent
   * @param {HTMLElement} adaptiveNode
   * @param {string} stylePre
   */
  addAdaptive(fTopParent = window, adaptiveNode, stylePre) {
    if (!fTopParent || !adaptiveNode || !stylePre) {
      return;
    }
    try {
      const { innerWidth } = fTopParent;
      const isViewXSm = innerWidth <= ViewMax.XSm;
      const isViewSm = innerWidth <= ViewMax.Sm;
      const isViewMed = innerWidth > ViewMax.Sm && innerWidth <= ViewMax.Md;
      const isViewLg = innerWidth > ViewMax.Md;
      const isViewXLg = innerWidth > ViewMax.Lg && innerWidth <= ViewMax.XLg;
      const isViewXXLg = innerWidth > ViewMax.XLg;
      /** @param {string} size */
      const getVClass = (size) => `${stylePre}view-${size}`;

      /**
       * @param {HTMLElement | null} node
       * @param {boolean} isView
       * @param {string} viewClass
       */
      const toggleAdaptive = (node, isView, viewClass) => {
        if (!node) {
          return;
        }
        /** @param {string} className */
        const toggleClass = (className) => node.classList.toggle(className);
        /** @param {string} className */
        const containsClass = (className) => node.classList.contains(className);
        const hasClass = containsClass(viewClass);
        isView && !hasClass && toggleClass(viewClass);
        !isView && hasClass && toggleClass(viewClass);
      };

      toggleAdaptive(adaptiveNode, isViewXSm, getVClass("xs"));
      toggleAdaptive(adaptiveNode, isViewSm, getVClass("s"));
      toggleAdaptive(adaptiveNode, isViewMed, getVClass("m"));
      toggleAdaptive(adaptiveNode, isViewLg, getVClass("l"));
      toggleAdaptive(adaptiveNode, isViewXLg, getVClass("xl"));
      toggleAdaptive(adaptiveNode, isViewXXLg, getVClass("xxl"));
    } catch (_e) {
      // do nothing
    }
  }

  onFrameResize() {
    const { isResizable, instanceId, id } = this.props;
    const frameDocument = this.getFrameDocument();
    if (isResizable && frameDocument && this.frameRef) {
      const offsetHeight = this.wrap && this.wrap.offsetHeight > 0 ? this.wrap.offsetHeight : 0;
      // NOTE: Only update height if this.wrap.offsetParent exists, else, the widget is probably hidden
      // and shouldn't resize because offsetHeight on hidden nodes will be 0
      if (this.wrap) {
        this.frameRef.height = `${offsetHeight}px`;
        dispatchEvent(instanceId, this, DispatchEvents.FrameResize, false, { height: offsetHeight, frameId: id });
      }
    }
  }

  frameAdaptiveListener() {
    const { stylePre, isResizable } = this.props;
    const frameDocument = this.getFrameDocument();
    if (frameDocument && this.frameRef && this.fTopParent) {
      const adaptSize = () => {
        this.addAdaptive(/** @type {Window} */ (this.fTopParent), frameDocument.documentElement, stylePre);
        isResizable && this.onFrameResize();
      };
      setTimeout(adaptSize, DelayTimer.FrameResizeDelayMS);
      adaptSize();
    }
  }

  createFramePortal() {
    const { children } = this.props;
    if (!this.fPortal) {
      this.fPortal = createPortal(Children.only(children), this.wrap);

      // Adding delay to frame load for headStyle fetch
      setTimeout(() => {
        this.setState({ fPortalCreated: true });
      }, DelayTimer.FramePortalInitDelayMS);
    }
  }

  render() {
    const { id, name, style, className, title, isResizable, frameHeight } = this.props;
    const { fPortalCreated } = this.state;
    return (
      <iframe
        id={id}
        name={name}
        style={style}
        className={className}
        title={title}
        aria-label={title}
        height={isResizable ? 0 : frameHeight}
        ref={(el) => (this.frameRef = el)}
      >
        {fPortalCreated && this.frameRef && this.fPortal}
      </iframe>
    );
  }
}

export default Frame;
